import { useMemo, useState, type FormEvent } from 'react';
import { ErrorBanner, SuccessBanner } from './ui';
import { Button, CheckboxInput, NumberInput, SelectInput, TextInput } from '../neon/controls';
import { useDomId } from '../hooks/useDomId';

export type FieldType = 'text' | 'number' | 'select' | 'checkbox' | 'readonly';

export interface FieldDef {
  key: string;
  label: string;
  type?: FieldType;
  options?: { value: string | number; label: string }[];
  hint?: string;
  allowEmpty?: boolean;
  emptyText?: string;
}

export interface EntityFormProps {
  fields: FieldDef[];
  initial: Record<string, any>;
  save: (patch: Record<string, any>) => Promise<unknown>;
  submitLabel?: string;
  onSaved?: () => void;
  onCancel?: () => void;
}

export function EntityForm({ fields, initial, save, submitLabel = 'Save changes', onSaved, onCancel }: EntityFormProps) {
  const formId = useDomId('form');
  const seed = useMemo(() => {
    const s: Record<string, any> = {};
    for (const f of fields) {
      const v = initial[f.key];
      if (f.type === 'checkbox') s[f.key] = Number(v) === 1;
      else if (f.type === 'number') s[f.key] = v == null || v === '' ? undefined : Number(v);
      else s[f.key] = v ?? '';
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, JSON.stringify(initial)]);

  const [form, setForm] = useState<Record<string, any>>(seed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const set = (key: string, value: any) => setForm((f) => ({ ...f, [key]: value }));

  const doSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const patch: Record<string, any> = {};
      for (const f of fields) {
        if (f.type === 'readonly') continue;
        const v = form[f.key];
        if (f.type === 'number') patch[f.key] = v === '' || v == null ? null : Number(v);
        else if (f.type === 'checkbox') patch[f.key] = v ? 1 : 0;
        else patch[f.key] = v;
      }
      await save(patch);
      setMessage('Saved.');
      onSaved?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void doSave();
  };

  return (
    <form className="entity-form" onSubmit={onSubmit}>
      <SuccessBanner message={message} />
      <ErrorBanner error={error} />
      <div className="form-grid">
        {fields.map((f) => {
          const fid = `${formId}-${f.key}`;
          if (f.type === 'readonly') {
            return <TextInput key={f.key} id={fid} label={f.label} value={String(form[f.key] ?? '')} onChange={() => {}} readOnly />;
          }
          if (f.type === 'select') {
            return (
              <SelectInput
                key={f.key}
                id={fid}
                label={f.label}
                value={form[f.key]}
                onChange={(v) => set(f.key, v)}
                options={f.options ?? []}
                allowEmpty={f.allowEmpty}
                emptyText={f.emptyText}
              />
            );
          }
          if (f.type === 'checkbox') {
            return <CheckboxInput key={f.key} id={fid} label={f.label} checked={!!form[f.key]} onChange={(v) => set(f.key, v)} />;
          }
          if (f.type === 'number') {
            return <NumberInput key={f.key} id={fid} label={f.label} value={form[f.key] ?? undefined} onChange={(v) => set(f.key, v)} />;
          }
          return <TextInput key={f.key} id={fid} label={f.label} value={form[f.key] ?? ''} onChange={(v) => set(f.key, v)} helper={f.hint} />;
        })}
      </div>
      <div className="form-actions">
        <Button id={`${formId}-submit`} text={saving ? 'Saving…' : submitLabel} onClick={() => void doSave()} loading={saving} />
        {onCancel ? <Button id={`${formId}-cancel`} text="Cancel" variant="tertiary" onClick={onCancel} disabled={saving} /> : null}
      </div>
    </form>
  );
}

/** Build read-mode label/value items from the same field defs used for editing. */
export function fieldsToReadItems(
  fields: FieldDef[],
  data: Record<string, any>,
): { label: string; value: string }[] {
  return fields.map((f) => {
    let v: any = data[f.key];
    if (f.type === 'select' && f.options) {
      const opt = f.options.find((o) => String(o.value) === String(v));
      v = opt ? opt.label : v;
    } else if (f.type === 'checkbox') {
      v = Number(v) === 1 ? 'Yes' : 'No';
    }
    return { label: f.label, value: v == null || v === '' ? '—' : String(v) };
  });
}
